import { FC, ReactNode, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { capitalCase } from 'change-case';
import { TbTimeline } from 'react-icons/tb';

import { DropdownOptionContent, DropdownPanel, DropdownPanelMenu, EmptySection } from '@/shared';
import { SetStateMethod } from '@/types';

interface MetricLineItemDropdownProps {
    children: ReactNode;
    emptyDescription?: string;
    emptySearchDescription?: string;
    emptyLabel?: string;
    lineItemLabels?: Partial<Record<string, string>>;
    lineItems: string[];
    optionIcon?: ReactNode;
    searchPlaceholder?: string;
    setChartedLineItem: SetStateMethod<string>;
    setTags: SetStateMethod<string[]>;
    tags: string[];
    toggleSelection?: boolean;
}

const MetricLineItemDropdown: FC<MetricLineItemDropdownProps> = ({
    children,
    emptyDescription = 'No line items are available for this financial view.',
    emptySearchDescription = 'No line items match your search.',
    emptyLabel = 'No Line Items',
    lineItemLabels,
    tags,
    setTags,
    lineItems,
    optionIcon = <TbTimeline size='1rem' />,
    searchPlaceholder = 'Search Line Items...',
    setChartedLineItem,
    toggleSelection = false,
}): JSX.Element => {
    const [input, setInput] = useState('');

    let searchedLineItems: string[];

    if (input !== '') {
        const normalizedInput = input.toLocaleLowerCase();

        searchedLineItems = lineItems.filter((lineItem) =>
            [lineItem, lineItemLabels?.[lineItem]].some((value) =>
                String(value ?? '')
                    .toLocaleLowerCase()
                    .includes(normalizedInput),
            ),
        );
    } else {
        searchedLineItems = lineItems;
    }

    return (
        <Dropdown
            disabled={!toggleSelection && tags.length >= 5}
            trigger={['click']}
            overlayStyle={{ minWidth: 16 * 20 }}
            popupRender={() => (
                <DropdownPanel
                    maxHeight={16 * 20}
                    minWidth={16 * 20}
                    search={{
                        placeholder: searchPlaceholder,
                        setValue: setInput,
                        value: input,
                    }}
                    style={{ marginTop: '0.75rem' }}
                >
                    <DropdownPanelMenu
                        disabled={!toggleSelection && tags.length >= 5}
                        selectable={false}
                        selectedKeys={tags}
                        onClick={({ domEvent, key }) => {
                            if (toggleSelection) {
                                domEvent.stopPropagation();
                                setTags(tags.includes(key) ? tags.filter((tag) => tag !== key) : [...tags, key]);
                                return;
                            }

                            setChartedLineItem(key);
                            setTags(Array.from(new Set([...tags, key])));
                        }}
                    >
                        {searchedLineItems.length ? (
                            searchedLineItems.map((lineItem) => (
                                <Menu.Item key={lineItem || '-'}>
                                    <DropdownOptionContent
                                        control={optionIcon}
                                        selected={tags.includes(lineItem)}
                                        title={lineItem ? (lineItemLabels?.[lineItem] ?? capitalCase(lineItem)) : '-'}
                                    />
                                </Menu.Item>
                            ))
                        ) : (
                            <EmptySection
                                height={16 * 12}
                                icon={optionIcon}
                                label={emptyLabel}
                                description={input ? emptySearchDescription : emptyDescription}
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default MetricLineItemDropdown;
